import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import Topbar from "../components/Topbar";
import { Bell, CheckCheck, Inbox, Filter } from "lucide-react";

const PAGE_SIZE = 50;

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // 'all' | 'unread'
  const nav = useNavigate();

  const reload = async (mode = filter) => {
    setLoading(true);
    try {
      const r = await api.get("/notifications", {
        params: { limit: PAGE_SIZE, unread_only: mode === "unread" },
      });
      setItems(r.data.items || []);
      setUnread(r.data.unread_count || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(filter); /* eslint-disable-next-line */ }, [filter]);

  const onItemClick = async (n) => {
    if (!n.read_at) {
      try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
    }
    if (n.link) nav(n.link);
    else reload();
  };

  const onMarkAll = async () => {
    if (unread === 0) return;
    try {
      await api.post("/notifications/read-all");
      reload();
    } catch {
      /* ignore */
    }
  };

  const fmtTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  };

  return (
    <div data-testid="notifications-page">
      <Topbar
        title="Notifications"
        subtitle={unread > 0 ? `${unread} unread` : "You're all caught up"}
        breadcrumb="Inbox"
      />

      <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-4">
        {/* Toolbar */}
        <div className="flex items-center justify-between gap-2 bg-[#141417] border border-zinc-800 rounded-lg p-2">
          <div
            data-testid="notif-filter-tabs"
            className="flex items-center gap-1 bg-[#0B0B0E] rounded p-0.5"
          >
            {[
              { id: "all",    label: "All",    count: null },
              { id: "unread", label: "Unread", count: unread },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setFilter(t.id)}
                data-testid={`notif-filter-${t.id}`}
                data-active={filter === t.id ? "true" : "false"}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded transition-colors ${
                  filter === t.id
                    ? "bg-[#141417] text-[#D4A968]"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                {t.id === "all" ? <Inbox className="w-3 h-3" /> : <Filter className="w-3 h-3" />}
                {t.label}
                {typeof t.count === "number" && t.count > 0 && (
                  <span className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-[#D4A968] text-black text-[9px] font-mono font-bold">
                    {t.count > 99 ? "99+" : t.count}
                  </span>
                )}
              </button>
            ))}
          </div>
          <button
            onClick={onMarkAll}
            disabled={unread === 0}
            data-testid="notif-mark-all-btn"
            className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-[#D4A968] border border-zinc-800 hover:border-[#D4A968] rounded px-2.5 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <CheckCheck className="w-3 h-3" />
            <span className="hidden sm:inline">Mark all read</span>
          </button>
        </div>

        {/* List */}
        <div className="bg-[#141417] border border-zinc-800 rounded-lg overflow-hidden">
          {loading ? (
            <div data-testid="notif-loading" className="divide-y divide-zinc-800/70">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="p-4 flex items-start gap-3 animate-pulse">
                  <div className="w-8 h-8 rounded-full bg-zinc-800 shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 w-2/3 bg-zinc-800 rounded" />
                    <div className="h-3 w-1/2 bg-zinc-800/70 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <div
              data-testid="notif-empty"
              className="p-12 text-center"
            >
              <Bell className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
              <div className="text-sm text-zinc-400">
                {filter === "unread" ? "No unread notifications." : "No notifications yet."}
              </div>
              <div className="text-xs text-zinc-600 mt-1">
                You'll see @mentions and updates here.
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-zinc-800/70" data-testid="notif-list">
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    onClick={() => onItemClick(n)}
                    data-testid={`notif-item-${n.id}`}
                    className={`w-full text-left p-4 flex items-start gap-3 transition-colors hover:bg-[#1B1B1F] ${
                      !n.read_at ? "bg-[#0B0B0E]" : ""
                    }`}
                  >
                    <div className="w-8 h-8 rounded-full bg-[#D4A968]/10 border border-[#D4A968]/30 flex items-center justify-center shrink-0">
                      <span className="text-[11px] font-bold text-[#D4A968]">
                        {(n.actor_name || "?").slice(0, 1).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="text-sm text-zinc-200 truncate">
                          <span className="font-semibold text-white">{n.actor_name || "Someone"}</span>
                          {" "}mentioned you in{" "}
                          <span className="text-zinc-300">{n.context}</span>
                        </div>
                        {!n.read_at && (
                          <span className="w-1.5 h-1.5 rounded-full bg-[#D4A968] shrink-0" />
                        )}
                      </div>
                      {n.text && (
                        <div className="text-xs text-zinc-400 italic mt-1 line-clamp-2">
                          &ldquo;{n.text}&rdquo;
                        </div>
                      )}
                      <div className="text-[10px] font-mono tracking-wider text-zinc-500 mt-1.5">
                        {fmtTime(n.created_at)}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
